import React, { useState, useEffect, useCallback } from 'react';
import { priceBookAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Loader2, Thermometer, Droplets, Zap, DoorOpen, Check,
  Wrench, Package, Layers, ChevronRight, Sparkles, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';

const ICON_MAP = {
  Thermometer, Droplets, Zap, DoorOpen,
};

const INDUSTRY_COLORS = {
  hvac: { bg: 'bg-orange-50 dark:bg-orange-950/30', border: 'border-orange-200 dark:border-orange-800', icon: 'text-orange-600 dark:text-orange-400' },
  plumbing: { bg: 'bg-blue-50 dark:bg-blue-950/30', border: 'border-blue-200 dark:border-blue-800', icon: 'text-blue-600 dark:text-blue-400' },
  electrical: { bg: 'bg-yellow-50 dark:bg-yellow-950/30', border: 'border-yellow-200 dark:border-yellow-800', icon: 'text-yellow-600 dark:text-yellow-400' },
  garage_doors: { bg: 'bg-slate-50 dark:bg-slate-950/30', border: 'border-slate-200 dark:border-slate-700', icon: 'text-slate-600 dark:text-slate-400' },
};

export default function StarterPacks({ onInstalled, activeIndustryColorKey }) {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewData, setPreviewData] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [installing, setInstalling] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const fetchPacks = useCallback(async () => {
    try {
      const res = await priceBookAPI.listStarterPacks();
      setPacks(res.data.starter_packs || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await priceBookAPI.getStarterPackSettings();
      setCollapsed(res.data.collapsed || false);
    } catch {
      // default to expanded
    } finally {
      setSettingsLoaded(true);
    }
  }, []);

  useEffect(() => { fetchPacks(); fetchSettings(); }, [fetchPacks, fetchSettings]);

  // Auto-collapse when a specific industry is filtered
  const isFiltered = !!activeIndustryColorKey;

  const toggleCollapsed = async () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      await priceBookAPI.updateStarterPackSettings({ collapsed: next });
    } catch {
      setCollapsed(!next);
    }
  };

  const handlePreview = async (packId) => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const res = await priceBookAPI.previewStarterPack(packId);
      setPreviewData({ ...res.data, _packId: packId });
    } catch {
      toast.error('Failed to load preview');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleInstall = async (packId) => {
    setInstalling(packId);
    try {
      const res = await priceBookAPI.installStarterPack(packId);
      const { created, skipped, name } = res.data;
      const totalCreated = created.categories + created.services + created.materials + created.templates;
      const totalSkipped = skipped.categories + skipped.services + skipped.materials + skipped.templates;
      toast.success(`${name} installed — ${totalCreated} items created${totalSkipped > 0 ? `, ${totalSkipped} skipped (already exist)` : ''}`);
      setPreviewOpen(false);
      setPreviewData(null);
      setCollapsed(true);
      fetchPacks();
      onInstalled?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Install failed');
    } finally {
      setInstalling(null);
    }
  };

  if (loading || !settingsLoaded) return null;
  if (packs.length === 0) return null;

  // When filtered to a specific industry, auto-collapse to compact bar
  const effectiveCollapsed = collapsed || isFiltered;

  // Filter packs to show only the matching one when an industry is active
  const visiblePacks = isFiltered
    ? packs.filter(p => p.id === activeIndustryColorKey)
    : packs;

  // Compact collapsed bar — intentional, not faded
  if (effectiveCollapsed) {
    return (
      <button
        onClick={isFiltered ? undefined : toggleCollapsed}
        className={`flex items-center gap-2.5 px-3.5 py-2 rounded-lg border transition-all duration-150 group w-full text-left ${
          isFiltered
            ? 'border-border/50 bg-muted/20 cursor-default'
            : 'border-border/60 hover:border-primary/30 hover:bg-muted/40 cursor-pointer'
        }`}
        data-testid="starter-packs-collapsed-bar"
        disabled={isFiltered}
      >
        <Sparkles className="w-3.5 h-3.5 text-amber-500/70 shrink-0" />
        <span className={`text-xs font-medium transition-colors ${isFiltered ? 'text-muted-foreground/60' : 'text-muted-foreground group-hover:text-foreground'}`}>
          Starter Packs
        </span>
        {packs.some(p => p.installed) && (
          <Badge variant="outline" className="text-[9px] gap-0.5 border-green-300/60 text-green-700 dark:border-green-700/60 dark:text-green-400 py-0">
            <Check className="w-2 h-2" /> Installed
          </Badge>
        )}
        {!isFiltered && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50 ml-auto shrink-0 group-hover:text-foreground transition-colors" />}
      </button>
    );
  }

  // Full expanded view — clean and balanced
  return (
    <div data-testid="starter-packs-section">
      <div className="flex items-center gap-2.5 mb-2.5">
        <Sparkles className="w-3.5 h-3.5 text-amber-500/70" />
        <span className="text-xs font-medium text-muted-foreground">Starter Packs</span>
        <button
          onClick={toggleCollapsed}
          className="ml-auto flex items-center gap-1 text-xs text-muted-foreground/70 hover:text-foreground transition-colors"
          data-testid="starter-packs-collapse-btn"
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
        {visiblePacks.map((pack) => {
          const colors = INDUSTRY_COLORS[pack.id] || INDUSTRY_COLORS.hvac;
          const Icon = ICON_MAP[pack.icon] || Wrench;
          return (
            <Card
              key={pack.id}
              className={`relative overflow-hidden transition-all duration-150 ${colors.border} ${
                pack.installed
                  ? 'opacity-60 grayscale-[20%]'
                  : 'hover:shadow-md hover:-translate-y-px cursor-pointer'
              }`}
              onClick={() => !pack.installed && handlePreview(pack.id)}
              data-testid={`starter-pack-${pack.id}`}
            >
              <CardContent className="p-3">
                <div className="flex items-center gap-2.5">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center`}>
                    <Icon className={`w-4 h-4 ${colors.icon}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <h4 className="font-semibold text-xs">{pack.name}</h4>
                      {pack.installed && (
                        <Badge variant="outline" className="text-[8px] gap-0.5 border-green-300/60 text-green-700 dark:border-green-700/60 dark:text-green-400 py-0" data-testid={`pack-installed-${pack.id}`}>
                          <Check className="w-2 h-2" /> Done
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[9px] text-muted-foreground">{pack.stats.services}s · {pack.stats.materials}m · {pack.stats.templates}t</span>
                    </div>
                  </div>
                  {!pack.installed && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50 shrink-0" />}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Preview & Install Dialog */}
      <Dialog open={previewOpen} onOpenChange={(o) => { if (!installing) { setPreviewOpen(o); if (!o) setPreviewData(null); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="starter-pack-preview">
          {previewLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : previewData ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  {previewData.name} Starter Pack
                </DialogTitle>
                <DialogDescription>{previewData.description}</DialogDescription>
              </DialogHeader>

              <div className="space-y-4 mt-2">
                <div>
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1.5">Categories ({previewData.categories.length})</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {previewData.categories.map((c, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{c.name}</Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1.5 flex items-center gap-1">
                    <Wrench className="w-3 h-3" /> Services ({previewData.services.length})
                  </h4>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {previewData.services.map((s, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1 px-2 rounded bg-muted/50">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-medium truncate">{s.name}</span>
                          {s.task_code && <span className="text-[10px] text-muted-foreground font-mono shrink-0">{s.task_code}</span>}
                        </div>
                        <span className="font-semibold shrink-0 ml-2">${s.price}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1.5 flex items-center gap-1">
                    <Package className="w-3 h-3" /> Materials ({previewData.materials.length})
                  </h4>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {previewData.materials.map((m, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1 px-2 rounded bg-muted/50">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-medium truncate">{m.name}</span>
                          {m.sku && <span className="text-[10px] text-muted-foreground font-mono shrink-0">{m.sku}</span>}
                        </div>
                        <span className="font-semibold shrink-0 ml-2">${m.price}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1.5 flex items-center gap-1">
                    <Layers className="w-3 h-3" /> Templates ({previewData.templates.length})
                  </h4>
                  <div className="space-y-2">
                    {previewData.templates.map((t, i) => (
                      <div key={i} className="p-2 rounded border bg-muted/30">
                        <div className="font-medium text-xs">{t.name}</div>
                        {t.description && <div className="text-[11px] text-muted-foreground">{t.description}</div>}
                        <div className="mt-1 space-y-0.5">
                          {t.components.map((c, j) => (
                            <div key={j} className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                              <Badge className={`${c.type === 'material' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'} text-[8px] px-1`}>
                                {c.type === 'material' ? 'MAT' : 'SVC'}
                              </Badge>
                              <span>{c.qty}x {c.name}</span>
                              <span className="ml-auto font-medium">${(c.qty * c.price).toFixed(0)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setPreviewOpen(false)} disabled={!!installing}>
                  Cancel
                </Button>
                <Button
                  onClick={() => handleInstall(previewData._packId)}
                  disabled={!!installing}
                  data-testid="install-starter-pack-btn"
                >
                  {installing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Install {previewData.name} Pack
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
